import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Clock, Camera, Film, Clapperboard, Image as ImageIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import { PacksSelection } from '../components/booking/PacksSelection';
import { MultiStudioSelection } from '../components/booking/MultiStudioSelection';
import { CalendlyCalendar } from '../components/booking/CalendlyCalendar';
import { MultiStudioCalendar } from '../components/booking/MultiStudioCalendar';
import { ExtraCameraSection } from '../components/booking/ExtraCameraSection';
import { ExtraMicrophoneSection } from '../components/booking/ExtraMicrophoneSection';
import { VideoEditingSection } from '../components/booking/VideoEditingSection';
import { ReelsEditingSection } from '../components/booking/ReelsEditingSection';
import { FileDeliverySection } from '../components/booking/FileDeliverySection';
import { PaymentOptions } from '../components/booking/PaymentOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const [step, setStep] = useState(0); // 0 = packs, 1 = studio, 2 = date/time, 3 = options, 4 = details
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState('');

  const [formData, setFormData] = useState({
    studio_id: '',
    background_id: '',
    date: '',
    start_time: '',
    duration_hours: 2,
    options: {
      cameras: 2,
      extra_cameras: 0,
      microphones: 2,
      extra_microphones: 0,
      video_editing: {
        enabled: false,
        duration: '',
        cameras: '1_camera',
        isCustomQuote: false
      },
      reels: [],
      file_delivery: 'on_site' // 'on_site' or 'google_drive'
    },
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    contact_method: 'whatsapp',
    payment_method: 'pay_now'
  });

  // Multi-studio configuration
  const [studioConfig, setStudioConfig] = useState(null);
  const [multiStudioSlots, setMultiStudioSlots] = useState({ studio1: [], studio2: [] });

  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const response = await axios.get(`${API}/studios`);
        setStudios(response.data);
      } catch (error) {
        console.error('Error fetching studios:', error);
        toast.error('Error loading studios');
      }
    };
    fetchStudios();
  }, []);

  useEffect(() => {
    if (formData.duration_hours >= 2) {
      calculatePrice();
    }
  }, [formData]);

  const calculatePrice = async () => {
    try {
      // Build options payload for new structure
      const optionsPayload = {
        cameras: formData.options.cameras,
        extra_cameras: formData.options.extra_cameras,
        microphones: formData.options.microphones,
        extra_microphones: formData.options.extra_microphones,
        video_editing: formData.options.video_editing,
        reels: formData.options.reels
      };

      const response = await axios.post(`${API}/bookings/calculate-price`, {
        duration_hours: formData.duration_hours,
        options: optionsPayload
      });
      setTotalPrice(response.data.total);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const formatIDR = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setFormData(prev => ({ ...prev, duration_hours: pack.hours }));
    setSelectedSlots([]);
    setStudioConfig(null);
    setMultiStudioSlots({ studio1: [], studio2: [] });
    // Immediately set the pack price
    setTotalPrice(pack.price);
  };

  const handleStudioConfigChange = (config) => {
    setStudioConfig(config);
    if (config && !config.useMultiStudio && config.singleStudio) {
      // Single studio mode - set form data directly
      setFormData(prev => ({
        ...prev,
        studio_id: config.singleStudio.studioId,
        background_id: config.singleStudio.backgroundId
      }));
      setSelectedBackground(config.singleStudio.backgroundId);
    }
  };

  const handleSelectSlot = (slotKey) => {
    // For single studio mode
    if (!studioConfig?.useMultiStudio) {
      const maxSlots = selectedPack ? selectedPack.hours / 2 : 1;
      
      if (selectedSlots.includes(slotKey)) {
        setSelectedSlots(selectedSlots.filter(s => s !== slotKey));
      } else if (selectedSlots.length < maxSlots) {
        setSelectedSlots([...selectedSlots, slotKey]);
        // Set date and time from first slot
        if (selectedSlots.length === 0) {
          const [date, time] = slotKey.split('_');
          setFormData({ ...formData, date, start_time: time });
        }
      } else {
        toast.error(`You can only select ${maxSlots} slot(s) for this pack`);
      }
    }
  };

  const handleMultiStudioSlotSelect = (studio, slotKey) => {
    const config = studioConfig?.multiStudio;
    if (!config) return;
    
    const maxSlots = studio === 'studio1' 
      ? Math.ceil(config.studio1.hours / 2)
      : Math.ceil(config.studio2.hours / 2);
    
    const currentSlots = multiStudioSlots[studio];
    
    if (currentSlots.includes(slotKey)) {
      setMultiStudioSlots(prev => ({
        ...prev,
        [studio]: currentSlots.filter(s => s !== slotKey)
      }));
    } else if (currentSlots.length < maxSlots) {
      setMultiStudioSlots(prev => ({
        ...prev,
        [studio]: [...currentSlots, slotKey]
      }));
    } else {
      toast.error(`You can only select ${maxSlots} slot(s) for ${studio === 'studio1' ? 'Studio 1' : 'Studio 2'}`);
    }
  };

  const validateStep4 = () => {
    const errors = [];
    
    if (!formData.customer_name || formData.customer_name.trim() === '') {
      errors.push('Full Name');
    }
    
    if (!formData.customer_email || formData.customer_email.trim() === '') {
      errors.push('Email');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customer_email)) {
        setShowValidationErrors(true);
        toast.error('Please enter a valid email');
        return false;
      }
    }
    
    if (!formData.customer_phone || formData.customer_phone.trim() === '') {
      errors.push('Phone number');
    }
    
    if (!formData.contact_method) {
      errors.push('Contact method (WhatsApp or Telegram)');
    }
    
    if (errors.length > 0) {
      setShowValidationErrors(true);
      toast.error(`Required fields missing: ${errors.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    // Validate all fields before submitting
    if (!validateStep4()) {
      return;
    }

    // Validate slots based on mode
    if (studioConfig?.useMultiStudio) {
      const allSlots = [...multiStudioSlots.studio1, ...multiStudioSlots.studio2];
      if (allSlots.length === 0) {
        toast.error('Please select time slots for both studios');
        return;
      }
    } else if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    setLoading(true);
    try {
      let bookingData;

      if (studioConfig?.useMultiStudio) {
        // Multi-studio booking
        const ms = studioConfig.multiStudio;
        
        // Convert slots for studio 1
        const studio1Slots = multiStudioSlots.studio1.map(slot => {
          const [date, start_time] = slot.split('_');
          return { date, start_time, studio_id: ms.studio1.studioId, background_id: ms.studio1.backgroundId };
        });
        
        // Convert slots for studio 2
        const studio2Slots = multiStudioSlots.studio2.map(slot => {
          const [date, start_time] = slot.split('_');
          return { date, start_time, studio_id: ms.studio2.studioId, background_id: ms.studio2.backgroundId };
        });
        
        const allTimeSlots = [...studio1Slots, ...studio2Slots];
        const firstSlot = allTimeSlots[0];
        
        bookingData = {
          ...formData,
          studio_id: ms.studio1.studioId, // Primary studio for compatibility
          background_id: ms.studio1.backgroundId,
          date: firstSlot.date,
          start_time: firstSlot.start_time,
          time_slots: allTimeSlots,
          multi_studio: {
            enabled: true,
            studio1: {
              studio_id: ms.studio1.studioId,
              background_id: ms.studio1.backgroundId,
              hours: ms.studio1.hours,
              slots: studio1Slots
            },
            studio2: {
              studio_id: ms.studio2.studioId,
              background_id: ms.studio2.backgroundId,
              hours: ms.studio2.hours,
              slots: studio2Slots
            }
          },
          payment_method: formData.payment_method
        };
      } else {
        // Single studio booking
        const timeSlots = selectedSlots.map(slot => {
          const [date, start_time] = slot.split('_');
          return { date, start_time };
        });

        const firstSlot = timeSlots[0];
        
        bookingData = {
          ...formData,
          date: firstSlot.date,
          start_time: firstSlot.start_time,
          time_slots: timeSlots,
          payment_method: formData.payment_method
        };
      }

      const bookingResponse = await axios.post(`${API}/bookings/create`, bookingData);
      const bookingId = bookingResponse.data.id;

      // If pay_later, redirect to confirmation with pending status
      if (formData.payment_method === 'pay_later') {
        window.location.href = `/confirmation?booking_id=${bookingId}&status=pending`;
        return;
      }

      // Pay now - create Stripe checkout session
      const originUrl = window.location.origin;
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        booking_id: bookingId,
        origin_url: originUrl
      });

      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Error creating booking');
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 0 && !selectedPack) {
      toast.error('Please select a pack');
      return;
    }
    if (step === 1) {
      if (!studioConfig) {
        toast.error('Please complete your studio selection');
        return;
      }
      if (studioConfig.useMultiStudio) {
        const ms = studioConfig.multiStudio;
        if (!ms?.studio1?.studioId || !ms?.studio1?.backgroundId || !ms?.studio2?.studioId || !ms?.studio2?.backgroundId) {
          toast.error('Please complete both studio selections');
          return;
        }
      } else {
        if (!studioConfig.singleStudio?.studioId || !studioConfig.singleStudio?.backgroundId) {
          toast.error('Please select a studio and background');
          return;
        }
      }
    }
    if (step === 2) {
      if (studioConfig?.useMultiStudio) {
        const ms = studioConfig.multiStudio;
        const requiredSlots1 = Math.ceil(ms.studio1.hours / 2);
        const requiredSlots2 = Math.ceil(ms.studio2.hours / 2);
        if (multiStudioSlots.studio1.length < requiredSlots1 || multiStudioSlots.studio2.length < requiredSlots2) {
          toast.error(`Please select all required time slots for both studios`);
          return;
        }
      } else if (selectedSlots.length === 0) {
        toast.error('Please select your time slots');
        return;
      }
    }
    if (step === 4) {
      if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.contact_method) {
        toast.error('Please fill in all required fields');
        return;
      }
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customer_email)) {
        toast.error('Please enter a valid email');
        return;
      }
    }
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <div className="min-h-screen pt-20" data-testid="booking-page">
      <div className="py-24 md:py-32 px-6 md:px-12 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium font-heading mb-6">
              Build Your <span className="text-primary">Session</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed tracking-wide font-light text-foreground-muted">
              Select your studio. Choose your setup. Define your production level.
            </p>
          </motion.div>

          <div
            className="p-8 md:p-12"
            style={{
              background: 'rgba(20, 20, 22, 0.7)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(234, 224, 213, 0.1)'
            }}
          >
            {/* Step Indicator */}
            <div className="flex justify-between mb-12">
              {['Pack', 'Studio', 'Date', 'Options', 'Details'].map((label, index) => (
                <div key={index} className="flex items-center flex-1">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-body font-bold transition-colors duration-300 ${
                        step >= index ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <span className="text-xs text-foreground-muted hidden sm:block">{label}</span>
                  </div>
                  {index < 4 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-colors duration-300 ${
                      step > index ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 0: Pack Selection */}
            {step === 0 && (
              <PacksSelection 
                selectedPack={selectedPack}
                onSelectPack={handleSelectPack}
                formatIDR={formatIDR}
              />
            )}

            {/* Step 1: Studio Selection */}
            {step === 1 && (
              <MultiStudioSelection
                studios={studios}
                totalHours={selectedPack?.hours || 2}
                studioConfig={studioConfig}
                onConfigChange={handleStudioConfigChange}
              />
            )}

            {/* Step 2: Calendar Selection */}
            {step === 2 && (
              studioConfig?.useMultiStudio ? (
                <MultiStudioCalendar
                  studioConfig={studioConfig}
                  multiStudioSlots={multiStudioSlots}
                  onSelectSlot={handleMultiStudioSlotSelect}
                />
              ) : (
                <CalendlyCalendar
                  selectedSlots={selectedSlots}
                  onSelectSlot={handleSelectSlot}
                  maxSlots={selectedPack ? selectedPack.hours / 2 : 1}
                />
              )
            )}

            {/* Step 3: Add-ons */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Customize Your Session</h2>
                <p className="text-center text-sm text-foreground-muted mb-8">
                  Add editing services to optimize your content
                </p>
                
                <div className="space-y-6">
                  {/* Section 1: Extra Camera */}
                  <ExtraCameraSection
                    extraCameras={formData.options.extra_cameras}
                    onChange={(value) => setFormData({
                      ...formData,
                      options: { ...formData.options, extra_cameras: value }
                    })}
                    formatIDR={formatIDR}
                  />

                  {/* Section 2: Extra Microphones */}
                  <ExtraMicrophoneSection
                    extraMicrophones={formData.options.extra_microphones}
                    onChange={(value) => setFormData({
                      ...formData,
                      options: { ...formData.options, extra_microphones: value }
                    })}
                    formatIDR={formatIDR}
                  />

                  {/* Section 3: Video Editing */}
                  <VideoEditingSection
                    videoEditing={formData.options.video_editing}
                    onChange={(value) => setFormData({
                      ...formData,
                      options: { ...formData.options, video_editing: value }
                    })}
                    formatIDR={formatIDR}
                  />

                  {/* Section 4: Reels Editing */}
                  <ReelsEditingSection
                    reels={formData.options.reels}
                    onChange={(value) => setFormData({
                      ...formData,
                      options: { ...formData.options, reels: value }
                    })}
                    formatIDR={formatIDR}
                  />

                  {/* Section 5: File Delivery */}
                  <FileDeliverySection
                    deliveryMethod={formData.options.file_delivery}
                    hasEditingOptions={formData.options.video_editing?.enabled || formData.options.reels?.length > 0}
                    onChange={(value) => setFormData({
                      ...formData,
                      options: { ...formData.options, file_delivery: value }
                    })}
                    formatIDR={formatIDR}
                  />
                </div>
              </motion.div>
            )}

            {/* Step 4: Customer Details */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Your Information</h2>
                <p className="text-center text-sm text-foreground-muted mb-8">
                  All fields are required <span className="text-red-500">*</span>
                </p>
                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Full Name <span className="text-red-500 text-lg">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => {
                        setFormData({ ...formData, customer_name: e.target.value });
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      required
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !formData.customer_name
                          ? 'border-red-500'
                          : 'border-border'
                      }`}
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                    {showValidationErrors && !formData.customer_name && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Email <span className="text-red-500 text-lg">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => {
                        setFormData({ ...formData, customer_email: e.target.value });
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      required
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !formData.customer_email
                          ? 'border-red-500'
                          : 'border-border'
                      }`}
                      placeholder="john.doe@example.com"
                      data-testid="customer-email-input"
                    />
                    {showValidationErrors && !formData.customer_email && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-sm font-semibold text-foreground mb-3 block">
                      Contact Method <span className="text-red-500 text-lg">*</span>
                    </label>
                    <div className={`grid grid-cols-2 gap-4 mb-3 ${
                      showValidationErrors && !formData.contact_method ? 'ring-2 ring-red-500 p-1' : ''
                    }`}>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, contact_method: 'whatsapp' });
                          if (showValidationErrors) setShowValidationErrors(false);
                        }}
                        className={`px-4 py-3 border-2 transition-all flex items-center justify-center gap-2 ${
                          formData.contact_method === 'whatsapp'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="contact-whatsapp-btn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span className="font-semibold">WhatsApp</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, contact_method: 'telegram' });
                          if (showValidationErrors) setShowValidationErrors(false);
                        }}
                        className={`px-4 py-3 border-2 transition-all flex items-center justify-center gap-2 ${
                          formData.contact_method === 'telegram'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="contact-telegram-btn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                        </svg>
                        <span className="font-semibold">Telegram</span>
                      </button>
                    </div>
                    {showValidationErrors && !formData.contact_method && (
                      <p className="text-xs text-red-500 mb-2">Please choose a contact method</p>
                    )}
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => {
                        setFormData({ ...formData, customer_phone: e.target.value });
                        if (showValidationErrors) setShowValidationErrors(false);
                      }}
                      required
                      className={`w-full px-4 py-3 bg-background border-2 text-foreground focus:border-primary focus:outline-none transition-colors duration-200 ${
                        showValidationErrors && !formData.customer_phone
                          ? 'border-red-500'
                          : 'border-border'
                      }`}
                      placeholder="+62 812 3456 7890"
                      data-testid="customer-phone-input"
                    />
                    {showValidationErrors && !formData.customer_phone && (
                      <p className="text-xs text-red-500 mt-1">This field is required</p>
                    )}
                    {formData.contact_method && formData.customer_phone && (
                      <p className="text-xs text-foreground-muted mt-2">
                        📱 We will contact you via {formData.contact_method === 'whatsapp' ? 'WhatsApp' : 'Telegram'}
                      </p>
                    )}
                  </div>

                  <div className="pt-6 border-t border-border">
                    <h3 className="font-heading font-semibold mb-4">Your Booking Summary</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-muted">Selected pack</span>
                        <span className="font-semibold">{selectedPack?.name} ({selectedPack?.hours}h)</span>
                      </div>
                      
                      <div className="border-t border-border pt-3">
                        <p className="text-foreground-muted mb-2">Reserved slots ({selectedSlots.length})</p>
                        <div className="space-y-2">
                          {selectedSlots.map((slot, index) => {
                            const [date, time] = slot.split('_');
                            const formattedDate = new Date(date).toLocaleDateString('en-US', { 
                              day: 'numeric', 
                              month: 'short',
                              year: 'numeric'
                            });
                            const hour = parseInt(time.split(':')[0]);
                            return (
                              <div key={index} className="flex justify-between text-xs bg-muted px-3 py-2">
                                <span>{formattedDate}</span>
                                <span className="font-semibold">{time} - {hour + 2}:00</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-3 border-t border-border">
                        <span className="text-foreground-muted">Total</span>
                        <span className="text-xl font-heading font-bold text-primary" data-testid="summary-total-price">
                          {formatIDR(totalPrice)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-muted mt-4">
                      Bookings are non-refundable within 48 hours of each session.
                    </p>
                  </div>

                  {/* Payment Options */}
                  <PaymentOptions
                    paymentMethod={formData.payment_method}
                    onChange={(value) => setFormData({ ...formData, payment_method: value })}
                    formatIDR={formatIDR}
                    totalPrice={totalPrice}
                  />
                </div>
              </motion.div>
            )}

            {/* Navigation & Price */}
            <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                {step > 0 && (
                  <button
                    onClick={prevStep}
                    className="px-6 py-3 border border-border text-foreground hover:border-primary hover:text-primary transition-colors duration-200"
                    data-testid="prev-step-btn"
                  >
                    Previous
                  </button>
                )}
              </div>

              <div className="text-center">
                {selectedPack && (
                  <>
                    <p className="text-xs uppercase tracking-[0.2em] font-body font-bold text-primary mb-2">
                      {selectedPack.name} - {selectedPack.hours}h
                    </p>
                    <p className="text-3xl font-heading font-bold" data-testid="total-price">
                      {formatIDR(selectedPack.price + (totalPrice - (formData.duration_hours * 1200000)))}
                    </p>
                  </>
                )}
              </div>

              <div>
                {step < 4 ? (
                  <button
                    onClick={nextStep}
                    className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200"
                    data-testid="next-step-btn"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-6 py-3 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
                    data-testid="submit-booking-btn"
                  >
                    {loading 
                      ? 'Processing...' 
                      : formData.payment_method === 'pay_later' 
                        ? 'Confirm Booking' 
                        : 'Proceed to Payment'
                    }
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}